<!-- Loading Indicator -->
<div *ngIf="loading" class="notes-loading">
  <i class="fa fa-spinner fa-spin"></i> Loading notes...
</div>

<!-- Notes List -->
<div class="notes-list" *ngIf="!loading">
  <div *ngIf="paginatedNotes.length === 0" class="no-notes">
    <p>No notes found</p>
  </div>

  <div
    *ngFor="let note of paginatedNotes"
    class="note-card"
    [ngClass]="{
      'note-open': note.status === 'Open',
      'note-resolved': note.status === 'Resolved',
      'note-follow-up-today': isFollowUpDueToday(note),
      'note-clickable': viewNote.observed,
    }"
    (click)="onCardClick(note)"
  >
    <!-- Status Badge -->
    <div
      class="note-status-badge"
      [ngClass]="note.status === 'Open' ? 'status-open' : 'status-resolved'"
    >
      {{ note.status }}
    </div>

    <!-- Driver Info (for client view) -->
    <div class="note-driver-info" *ngIf="showDriverInfo && (note.driverName || note.driverNo)">
      <i class="fa fa-user"></i>
      <span
        class="driver-name"
        *ngIf="note.driverName"
        [innerHTML]="getHighlightedText(note.driverName)"
      ></span>
      <span class="driver-no" *ngIf="note.driverNo">({{ note.driverNo }})</span>
    </div>

    <!-- Note Content -->
    <div class="note-body">
      <!-- General Note -->
      <div
        class="note-text"
        *ngIf="!isAccidentOrIncident(note.type) && !isDelayOrBreakdown(note.type)"
      >
        <span [innerHTML]="getHighlightedText(note.note)"></span>
      </div>

      <!-- Complaint: tagged internal user -->
      <div class="note-complaint-tag" *ngIf="isComplaint(note.type) && note.taggedUserName">
        <i class="fa fa-user"></i>
        <span>Tagged: {{ note.taggedUserName }}</span>
      </div>

      <!-- Accident/Incident Note -->
      <div *ngIf="isAccidentOrIncident(note.type)" class="note-details-grid">
        <div class="note-section" *ngIf="note.anyInjuries !== undefined">
          <strong>Injuries/Tow-aways/Fatalities</strong>
          <span
            class="note-value"
            [class.value-yes]="note.anyInjuries"
            [class.value-no]="!note.anyInjuries"
            >{{ note.anyInjuries ? "YES" : "NO" }}</span
          >
          <!-- Which of the three the Yes answer refers to (new notes only). -->
          <span class="note-flags" *ngIf="note.anyInjuries && hasAnyInjuryFlag(note)">
            <span class="note-flag-chip" *ngIf="note.hasInjuries">Injuries</span>
            <span class="note-flag-chip" *ngIf="note.hasTowAway">Tow away</span>
            <span class="note-flag-chip" *ngIf="note.hasFatality">Fatality</span>
          </span>
        </div>
        <div class="note-section" *ngIf="note.anotherVehicleInvolved !== undefined">
          <strong>Another Vehicle Involved</strong>
          <span
            class="note-value"
            [class.value-yes]="note.anotherVehicleInvolved"
            [class.value-no]="!note.anotherVehicleInvolved"
            >{{ note.anotherVehicleInvolved ? "YES" : "NO" }}</span
          >
        </div>
        <div class="note-section" *ngIf="note.driverCitation !== undefined">
          <strong>Driver Citation</strong>
          <span
            class="note-value"
            [class.value-yes]="note.driverCitation"
            [class.value-no]="!note.driverCitation"
            >{{ note.driverCitation ? "YES" : "NO" }}</span
          >
        </div>
        <div class="note-section" *ngIf="note.weather">
          <strong>Weather</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.weather)"></span>
        </div>

        <div class="note-section" *ngIf="note.tractorNumber || note.trailerNumber">
          <strong>Tractor / Trailer</strong>
          <span class="note-value">
            <span
              *ngIf="note.tractorNumber"
              [innerHTML]="getHighlightedText(note.tractorNumber)"
            ></span>
            <span *ngIf="note.tractorNumber && note.trailerNumber"> / </span>
            <span
              *ngIf="note.trailerNumber"
              [innerHTML]="getHighlightedText(note.trailerNumber)"
            ></span>
          </span>
        </div>
        <div class="note-section" *ngIf="note.shipperTrailerInformation">
          <strong>Shipper Trailer Info</strong>
          <span
            class="note-value"
            [innerHTML]="getHighlightedText(note.shipperTrailerInformation)"
          ></span>
        </div>
        <div class="note-section" *ngIf="note.loadedEmpty">
          <strong>Loaded/Empty</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.loadedEmpty)"></span>
        </div>
        <div class="note-section" *ngIf="note.productName">
          <strong>Product</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.productName)"></span>
        </div>

        <div
          class="note-section"
          *ngIf="note.accidentDate || note.accidentTime || note.accidentCity || note.accidentState"
        >
          <strong>Time / Place</strong>
          <span class="note-value">
            <span
              *ngIf="note.accidentDate"
              [innerHTML]="getHighlightedText(formatDate(note.accidentDate))"
            ></span>
            <span *ngIf="note.accidentTime">
              &nbsp;<span [innerHTML]="getHighlightedText(formatTime(note.accidentTime))"></span
            ></span>
            <span
              *ngIf="
                (note.accidentDate || note.accidentTime) &&
                (note.accidentCity || note.accidentState)
              "
            >
              -
            </span>
            <span
              *ngIf="note.accidentCity"
              [innerHTML]="getHighlightedText(note.accidentCity)"
            ></span
            ><span *ngIf="note.accidentCity && note.accidentState">, </span
            ><span
              *ngIf="note.accidentState"
              [innerHTML]="getHighlightedText(note.accidentState)"
            ></span>
          </span>
        </div>
        <div class="note-section" *ngIf="note.timeZone">
          <strong>Time Zone</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.timeZone)"></span>
        </div>
        <div class="note-section" *ngIf="note.accidentLocation">
          <strong>Accident Location</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.accidentLocation)"></span>
        </div>
        <div class="note-section" *ngIf="note.assignedCustomer">
          <strong>Assigned Customer</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.assignedCustomer)"></span>
        </div>
        <div class="note-section" *ngIf="note.photosToSafety">
          <strong>Photos to Safety</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.photosToSafety)"></span>
        </div>

        <div
          class="note-section two-columns"
          *ngIf="note.driverCitation && note.citationDescription"
        >
          <strong>Citation Description</strong>
          <span
            class="note-value"
            [innerHTML]="getHighlightedText(note.citationDescription)"
          ></span>
        </div>

        <div class="note-section full-width" *ngIf="note.anyInjuries && note.injuriesDescription">
          <strong>Injuries/Tow-aways/Fatalities Details</strong>
          <span
            class="note-value"
            [innerHTML]="getHighlightedText(note.injuriesDescription)"
          ></span>
        </div>

        <div
          class="note-section full-width"
          *ngIf="note.anotherVehicleInvolved && note.anotherVehicleDescription"
        >
          <strong>Another Vehicle Details</strong>
          <span
            class="note-value"
            [innerHTML]="getHighlightedText(note.anotherVehicleDescription)"
          ></span>
        </div>

        <div class="note-section full-width" *ngIf="note.accidentDescription">
          <strong>Accident Description</strong>
          <span
            class="note-value"
            [innerHTML]="getHighlightedText(note.accidentDescription)"
          ></span>
        </div>

        <div class="note-section full-width" *ngIf="note.damageDescription">
          <strong>Damage Description</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.damageDescription)"></span>
        </div>

        <div class="note-section full-width" *ngIf="note.note">
          <strong>Note</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.note)"></span>
        </div>
      </div>

      <!-- Delay/Breakdown Note -->
      <div *ngIf="isDelayOrBreakdown(note.type)" class="note-details-grid">
        <div class="note-section" *ngIf="note.clientName || note.billToName">
          <strong>Client / Bill To</strong>
          <span class="note-value">
            <span *ngIf="note.clientName" [innerHTML]="getHighlightedText(note.clientName)"></span>
            <span *ngIf="note.clientName && note.billToName"> / </span>
            <span *ngIf="note.billToName" [innerHTML]="getHighlightedText(note.billToName)"></span>
          </span>
        </div>
        <div class="note-section" *ngIf="note.loadedEmpty">
          <strong>Loaded/Empty</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.loadedEmpty)"></span>
        </div>
        <div class="note-section" *ngIf="note.productName">
          <strong>Product</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.productName)"></span>
        </div>

        <div class="note-section" *ngIf="note.tripNumber">
          <strong>Trip Number</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.tripNumber)"></span>
        </div>
        <div class="note-section" *ngIf="note.customerReferenceNumber">
          <strong>Customer Ref #</strong>
          <span
            class="note-value"
            [innerHTML]="getHighlightedText(note.customerReferenceNumber)"
          ></span>
        </div>
        <div class="note-section" *ngIf="note.eta">
          <strong>ETA</strong>
          <span class="note-value">{{ note.eta | date: "MM/dd/yy h:mm a" }}</span>
        </div>
        <div class="note-section" *ngIf="note.truckTrailerIssue">
          <strong>Issue Type</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.truckTrailerIssue)"></span>
        </div>
        <div
          class="note-section"
          *ngIf="note.hosRemaining !== undefined && note.hosRemaining !== null"
        >
          <strong>HOS Remaining</strong>
          <span class="note-value"
            ><span [innerHTML]="getHighlightedText(note.hosRemaining.toString())"></span>
            hours</span
          >
        </div>
        <div class="note-section" *ngIf="note.roadCallNumber">
          <strong>Road Call Number</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.roadCallNumber)"></span>
        </div>

        <div class="note-section full-width" *ngIf="note.internalNotes">
          <strong>Internal Notes</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.internalNotes)"></span>
        </div>

        <div class="note-section full-width" *ngIf="note.clientNotes">
          <strong>Client Notes</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.clientNotes)"></span>
        </div>
      </div>

      <!-- Driver Release Note -->
      <div *ngIf="isDriverRelease(note.type)" class="note-details-grid">
        <div class="note-section" *ngIf="note.driverLocation">
          <strong>Location</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.driverLocation)"></span>
        </div>
        <div class="note-section" *ngIf="note.loadedEmpty">
          <strong>Loaded/Empty</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.loadedEmpty)"></span>
        </div>
        <div
          class="note-section"
          *ngIf="note.requestTimeOff !== undefined && note.requestTimeOff !== null"
        >
          <strong>Wants Time Off</strong>
          <span class="note-value">{{ note.requestTimeOff ? "Yes" : "No" }}</span>
        </div>
        <div class="note-section" *ngIf="note.trailerDropoffLocation">
          <strong>Trailer Dropoff Location</strong>
          <span
            class="note-value"
            [innerHTML]="getHighlightedText(note.trailerDropoffLocation)"
          ></span>
        </div>
        <div class="note-section" *ngIf="note.nextAvailable">
          <strong>Next Available</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.nextAvailable)"></span>
        </div>
        <div class="note-section" *ngIf="note.productName">
          <strong>Product</strong>
          <span class="note-value" [innerHTML]="getHighlightedText(note.productName)"></span>
        </div>
      </div>
    </div>

    <!-- Note Footer -->
    <div class="note-footer">
      <div class="note-footer-left">
        <span class="note-type-badge">{{ note.type }}</span>

        <!-- Follow-up Date Indicator -->
        <span
          *ngIf="note.followUpDate"
          class="follow-up-badge"
          [class.follow-up-today]="isFollowUpDueToday(note)"
          [title]="'Follow-up: ' + (note.followUpDate | date: 'MM/dd/yyyy')"
        >
          <i class="fa fa-bell" *ngIf="isFollowUpDueToday(note)"></i>
          <i class="fa fa-clock-o" *ngIf="!isFollowUpDueToday(note)"></i>
          {{ note.followUpDate | date: "MM/dd/yyyy" }}
        </span>

        <!-- Email Status Indicators -->
        <div
          class="email-status-indicators"
          *ngIf="
            isAccidentOrIncident(note.type) ||
            isDelayOrBreakdown(note.type) ||
            isGeneral(note.type) ||
            isDriverRelease(note.type)
          "
        >
          <span
            *ngIf="isAccidentOrIncident(note.type) && note.emailedToSafety"
            class="email-status-badge"
            [title]="'Emailed to Safety: ' + (note.emailedToSafety | date: 'MM/dd/yyyy HH:mm')"
          >
            <i class="fa fa-envelope"></i> Safety
          </span>
          <span
            *ngIf="isAccidentOrIncident(note.type) && note.emailedFormToDriver"
            class="email-status-badge"
            [title]="
              'Form Emailed to Driver: ' + (note.emailedFormToDriver | date: 'MM/dd/yyyy HH:mm')
            "
          >
            <i class="fa fa-envelope"></i> Form
          </span>
          <span
            *ngIf="
              (isDelayOrBreakdown(note.type) ||
                isGeneral(note.type) ||
                isDriverRelease(note.type)) &&
              note.emailedToClient
            "
            class="email-status-badge"
            [title]="'Emailed to Client: ' + (note.emailedToClient | date: 'MM/dd/yyyy HH:mm')"
          >
            <i class="fa fa-envelope"></i> Client
          </span>
          <span
            *ngIf="
              (isDelayOrBreakdown(note.type) ||
                isGeneral(note.type) ||
                isDriverRelease(note.type)) &&
              note.emailedToDriver
            "
            class="email-status-badge"
            [title]="'Emailed to Driver: ' + (note.emailedToDriver | date: 'MM/dd/yyyy HH:mm')"
          >
            <i class="fa fa-envelope"></i> Driver
          </span>
        </div>
      </div>
      <div class="note-footer-right">
        <div class="note-meta">
          <span class="note-author">{{ note.createdByUserName }}</span>
          <span class="note-date">{{ note.dateCreatedUtc | date: "MM/dd/yyyy HH:mm" }}</span>
        </div>
        <div class="note-actions" *ngIf="canEdit">
          <button class="btn-icon" (click)="onEditClick(note, $event)">
            <i class="fa fa-pencil"></i>
          </button>
          <button class="btn-icon btn-danger" (click)="onDeleteClick(note, $event)">
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Pagination -->
<div *ngIf="notes.length > pageSize" class="pagination-controls">
  <div class="pagination-info">
    Showing {{ (currentPage - 1) * pageSize + 1 }} -
    {{ Math.min(currentPage * pageSize, notes.length) }} of {{ notes.length }} notes
  </div>
  <div class="pagination-buttons">
    <button
      class="btn btn-sm btn-secondary"
      (click)="previousPage()"
      [disabled]="currentPage === 1"
    >
      <i class="fa fa-chevron-left"></i> Previous
    </button>
    <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
    <button
      class="btn btn-sm btn-secondary"
      (click)="nextPage()"
      [disabled]="currentPage === totalPages"
    >
      Next <i class="fa fa-chevron-right"></i>
    </button>
  </div>
</div>
